<template>
  <div>
    <attach-files-dialog
      :opened.sync="attachFilesDialogOpened"
      @upload-all="uploadAll"
    />

    <preview-document-dialog
      v-if="currentCompanyId"
      :opened.sync="previewDocumentDialogOpened"
      :previewType="previewType"
      :previewLoaded="previewLoaded"
      :documentPreviewData="documentPreviewData"
      :selectedMessageId="selectedMessageId"
      :selectedCompanyId="currentCompanyId"
      :selectedDocumentLink.sync="selectedDocumentLink"
      @refresh-conversation="refreshConversation"
    />

    <v-alert
      v-if="selectedConversation && selectedConversation.advisor_out_of_office"
      :value="true"
      outline
      block
      :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
      color="primary"
      class="text-xs-left mb-2"
      style="white-space: pre-line"
    >
      {{ selectedConversation.advisor_out_of_office_message }}
    </v-alert>

    <v-alert
      v-else-if="selectedConversation && isEmpty(selectedConversation.messages)"
      :value="true"
      outline
      block
      :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
      color="primary"
      class="text-xs-left mb-4 mt-4"
    >
      {{ $t('inbox.noMessages') }}
    </v-alert>

    <aside v-if="selectedThreadId" class="conversation-box__threads">
      <header class="conversation-box__threads__header">
        <sc-flex :is-y-centered="true" :gap="8">
          <sc-h4>Thread</sc-h4>
        </sc-flex>
        <sc-button-group>
          <sc-button
            @click="closeThread()"
            size="small"
            type="secondary"
            icon-left="close"
            :is-icon-only="true"
            >Close</sc-button
          >
        </sc-button-group>
      </header>
      <section class="conversation-box__threads__container">
        <sc-flex v-if="selectedThreadLoading" :is-centered="true" :padding="16">
          <v-progress-circular
            indeterminate
            size="24"
            color="primary"
          ></v-progress-circular>
        </sc-flex>
        <div
          v-else
          class="conversation-box__threads__content"
          ref="threadContainer"
        >
          <!-- Partial: Selected -->
          <SxChatBubble
            v-if="selectedThreadMessage"
            :key="i"
            :current-user="currentUser"
            :preview-document="previewDocument"
            :open-thread="openThread"
            :selected-message-id="selectedThreadId"
            :is-reply="true"
            :message="selectedThreadMessage"
          ></SxChatBubble>

          <!-- Partial: Bubbles -->
          <SxChatBubble
            v-for="(message, i) in selectedThreadMessages"
            :key="i"
            :current-user="currentUser"
            :preview-document="previewDocument"
            :open-thread="openThread"
            :is-reply="true"
            :message="message"
          ></SxChatBubble>
        </div>
        <footer class="conversation-box__threads__footer">
          <form>
            <sc-button-group>
              <sc-button
                @click="sendReply()"
                size="small"
                :is-disabled="sendingReply"
                :is-loading="sendingReply"
                type="primary"
                icon-right="send"
                >Send</sc-button
              >
            </sc-button-group>
            <textarea
              placeholder="Message"
              v-model="newReply"
              class="reply-textarea"
              :class="sendingReply && 'reply-textarea--disabled'"
            ></textarea>
          </form>
        </footer>
      </section>
    </aside>

    <div
      ref="messageContainer"
      class="inbox-new-container"
      :class="isScrolling ? 'inbox-new-container--scrolling' : ''"
    >
      <!-- Partial: Bubble(s) -->
      <SxChatBubble
        v-for="(message, i) in selectedMessages"
        :key="i"
        :current-user="currentUser"
        :preview-document="previewDocument"
        :open-thread="openThread"
        :message="message"
      ></SxChatBubble>
    </div>

    <template v-if="!currentUser.readonly_access">
      <quill-editor
        id="new-message-editor"
        v-model="newMessage"
        :options="editorOption"
        :disabled="sending"
        class="ql-message-input default-input"
      />

      <div class="action-btn-container">
        <v-layout>
          <v-tooltip top v-if="!isAdvisor && messageTagsEnabled">
            <v-radio-group
              v-model="messageTag"
              class="py-3 px-2 mt-0"
              slot="activator"
              hide-details
              row
            >
              <v-radio label="Not Urgent" value="general" class="my-0" />
              <v-radio label="Urgent" value="urgent" class="my-0" />
              <v-radio label="Emergency" value="emergency" class="my-0" />
            </v-radio-group>
            <span>Please tag the message accordingly</span>
          </v-tooltip>
        </v-layout>

        <v-layout align-center>
          <v-btn
            class="primary text-capitalize"
            @click="sendMessage"
            :loading="sending"
          >
            <span>{{ $t('inbox.send') }}</span>
          </v-btn>

          <div>
            <v-btn
              class="mx-2 text-capitalize"
              @click="openImportDocumentsDialog()"
              flat
            >
              <v-icon small>attach_file</v-icon>
              {{ $t('inbox.attachFile') }}
            </v-btn>
          </div>
        </v-layout>
      </div>
    </template>
  </div>
</template>

<script>
import API from '@/api';
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import AttachFilesDialog from '@/components/dialogs/AttachFilesDialog';
import PreviewDocumentDialog from '@/components/dialogs/PreviewDocumentDialog';
import { supportedExtensionsForPreview } from '@/constants/variables';
import firebase from 'firebase';
import SxChatBubble from '@/components/sx/SxChatBubble';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';

export default {
  components: {
    AttachFilesDialog,
    PreviewDocumentDialog,
    SxChatBubble,
  },

  mixins: [FirmPreferenceMixins],

  props: {
    currentCompanyId: {
      type: Number,
      default: null,
    },

    currentCompany: {
      type: Object,
      default: null,
    },

    shouldFocusEditor: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedThreadId: null,
      selectedThreadMessages: [],
      selectedThreadLoading: false,
      isScrolling: true,
      sendingReply: false,
      firstRender: false,
      newReply: '',
      pageSize: 5,
      currentPage: 1,
      supportedExtensionsForPreview,
      selectedConversation: null,
      messageTag: 'general',
      loading: false,
      newMessage: '',
      editorOption: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar:
            window.innerWidth >= 768
              ? [
                  ['bold', 'italic', 'underline'],
                  [{ indent: '-1' }, { indent: '+1' }],
                  ['link'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                ]
              : [['bold', 'italic', 'underline']],
        },
        placeholder: this.$t('inbox.message'),
        bounds: '.ql-message-input',
      },
      sending: false,

      attachFilesDialogOpened: false,

      selectedMessageId: null,
      selectedDocumentLink: '',
      previewType: '',
      previewLoaded: false,
      documentPreviewData: null,
      previewDocumentDialogOpened: false,
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
      isAdvisor: 'isAdvisor',
      myConversations: 'myConversations',
      defaultCompany: 'defaultCompany',
    }),
    ...mapState('companies', ['companies']),
    allMessages() {
      if (!this.selectedConversation) return [];

      return (
        this.selectedConversation.messages.filter(
          message => !message.scheduled,
        ) || []
      );
    },
    selectedThreadMessage() {
      const selectedThreadMessage = this.allMessages.find(
        m => m.id === this.selectedThreadId,
      );

      return selectedThreadMessage;
    },
    selectedMessages() {
      const filteredMessages = this.allMessages
        .map(message => {
          message.threadMessages = [];

          return message;
        })
        .filter(message => !!message && !message.message_thread_id);

      this.allMessages.forEach(message => {
        const threadMessages = this.allMessages.filter(
          m => message.id === m.message_thread_id,
        );

        message.threadMessages = threadMessages || [];
      });

      return filteredMessages;
    },
  },

  methods: {
    openThread(messageId) {
      console.log({ messageId });
      this.selectedThreadId = messageId;
    },
    closeThread() {
      this.selectedThreadId = null;
      this.selectedThreadMessages = [];
      this.scrollToBottom();
    },
    async loadThreadMessages(isSilent = false) {
      if (!this.selectedThreadId) return void 0;

      try {
        this.selectedThreadLoading = !isSilent;

        const { data } = await API.conversation.getThreadMessages(
          this.selectedConversation.id,
          this.selectedThreadId,
        );

        this.selectedThreadMessages = data.messages;
      } catch (error) {
        console.error(error);
      } finally {
        this.selectedThreadLoading = false;
        this.scrollThreadsToBottom();
        API.conversation.updateThreadAsSeen({
          conversationId: this.selectedConversation.id,
          threadId: this.selectedThreadId,
        });
      }
    },
    async sendReply() {
      if (!this.newReply) return;

      this.sendingReply = true;

      try {
        await API.conversation.sendMessage({
          text: this.newReply
            .trim()
            .split('\n')
            .join('<br/>'),
          conversationId: this.selectedConversation.id,
          messageThreadId: this.selectedThreadId,
        });

        const newMessage = {
          id: null,
          user_id: this.currentUser.id,
          user_name: this.currentUser.full_name,
          text: this.newReply,
          avatar_url: this.currentUser.avatar,
          created_at: moment(),
        };

        this.selectedThreadMessages = [
          ...this.selectedThreadMessages,
          newMessage,
        ];
      } catch (error) {
        console.error(error);
      } finally {
        this.newReply = '';
        this.sendingReply = false;
        this.scrollThreadsToBottom();
        this.loadThreadMessages(true);
      }
    },
    isEmpty,
    moment,
    scrollToBottom() {
      const scrollDelay = this.isScrolling ? 240 : 0;

      setTimeout(() => {
        const scrollElement = this.$refs.messageContainer;

        if (!scrollElement) return;
        if (!this.selectedThreadId) this.setScrollPosition();

        // NOTE @chris This is necessary to make only non-initial scrolls smooth and to make sure things are mounted.
        setTimeout(() => {
          this.isScrolling = false;
        }, 100);
      }, scrollDelay);
    },
    openLastUnread() {
      const lastUnreadMessageId = this.selectedConversation.messages.filter(
        message => message.unseen_user_ids.includes(this.currentUser.id),
      )[0].id;

      const lastUnreadMessage = this.selectedConversation.messages.find(
        m => m.id === lastUnreadMessageId,
      );
      const isLastMessageThread =
        lastUnreadMessage && lastUnreadMessage.message_thread_id;

      if (isLastMessageThread) {
        this.openThread(lastUnreadMessage.message_thread_id);
        this.setScrollPosition();
      }
    },
    setScrollPosition() {
      const scrollElement = this.$refs.messageContainer;

      if (!scrollElement) return;

      scrollElement.scrollTop = scrollElement.scrollHeight;

      this.updateConversationAsSeen();
      this.$store.dispatch(
        'updateConversationAsSeen',
        this.selectedConversation.id,
      );
    },
    showDivider(idx) {
      return (
        idx === 0 ||
        !moment(this.selectedMessages[idx].created_at).isSame(
          moment(this.selectedMessages[idx - 1].created_at),
          'day',
        )
      );
    },
    formatDayTitle(message) {
      const messageMomentDate = moment(message.created_at);
      return messageMomentDate.format('ddd, MMM D');
    },
    async uploadAll(documentsToImport) {
      let path = '';
      this.sending = true;

      try {
        for (const documentToImport of documentsToImport) {
          const companyId = this.currentCompanyId;

          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId,
          });

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.integration.processDocument({
            id,
            documentName: documentToImport.name,
            companyId,
            path: '/Attachments',
            conversationId: this.selectedConversation.id,
          });

          if (response.data.error) {
            console.log(response.data.error);
          } else if (isEmpty(path)) {
            path = response.data.document_location;
          } else {
            path += '&!&'.concat(`${response.data.document_location}`);
          }
        }

        this.$store.dispatch('application/setSuccessNotification', {
          message: `You have successfully uploaded ${
            documentsToImport.length
          } document${documentsToImport.length > 1 ? 's' : ''}!`,
        });

        const response2 = await API.conversation.sendMessage({
          text: `$$attached$$${path}`,
          conversationId: this.selectedConversation.id,
          userId: this.currentCompanyId,
        });

        if (response2.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem.',
          });
          console.log(response2.data.error);
        } else {
          await this.refreshConversation();
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Attachment unsuccessful. Please try again.',
        });
      } finally {
        this.sending = false;
      }
    },

    getDocumentLinks(message) {
      return message.text.split('$$')[2].split('&!&');
    },

    getDocumentName(link) {
      return link.split('/')[link.split('/').length - 1];
    },

    endsWithAny(suffixes, string) {
      return suffixes.find(function(suffix) {
        return string.toLowerCase().endsWith(suffix);
      });
    },

    async previewDocument(documentLink, message) {
      this.selectedMessageId = message.id;
      this.previewLoaded = false;

      try {
        const response = await API.conversation.downloadAttachment(
          documentLink,
        );

        if (response.data.document) {
          this.previewDocumentDialogOpened = true;
          this.companies.find(c => c.name === documentLink.split('/')[2]);

          this.selectedDocumentLink = documentLink;
          this.previewType = this.endsWithAny(
            this.supportedExtensionsForPreview,
            this.getDocumentName(documentLink),
          );
          this.documentPreviewData = response.data.document.data.link;
        } else {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem downloading the document.',
            errorToSend: response.data.error,
            userEmail: this.currentUser.email,
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.previewLoaded = true;
      }
    },

    async sendMessage() {
      if (this.newMessage && !this.sending) {
        this.sending = true;

        const response = await API.conversation.sendMessage({
          text: this.newMessage
            .trim()
            .split('\n')
            .join('<br/>'),
          conversationId: this.selectedConversation.id,
          hasReminder: this.remindClient,
          messageTag: this.messageTag,
        });

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem.',
          });

          console.log(response.data.error);
        } else {
          await this.refreshConversation();

          this.sending = false;
          this.newMessage = '';
        }
      }
    },

    async openImportDocumentsDialog() {
      this.attachFilesDialogOpened = true;
    },
    async refreshConversation() {
      try {
        this.loading = true;
        const response = await API.conversation.getConversation(
          this.currentCompany.relevant_conversation_id,
        );
        this.selectedConversation = Object.assign({}, response.data);
        console.log(this.selectedConversation);
        this.updateConversationAsSeen();
        this.openLastUnread();
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
        this.scrollToBottom();
      }
    },

    subscribeToNewMessages() {
      const database = firebase.database();

      this.newMessagesRef = database.ref(
        `users/${this.currentUser.id}/newMessages`,
      );

      this.listeningToNewMessages = this.newMessagesRef.on(
        'value',
        async () => {
          console.log('trigger event');
          if (this.currentUser.id && !this.loading) {
            await this.refreshConversation();
          }
        },
      );
    },

    unsubscribeToNewMessages() {
      if (this.listeningToNewMessages && this.newMessagesRef) {
        this.newMessagesRef.off('value', this.listeningToNewMessages);
      }
    },

    focusEditor() {
      this.$nextTick(() => {
        setTimeout(() => {
          const editor = document.querySelector(
            '#new-message-editor .ql-editor',
          );
          editor.scrollIntoView();
          editor.focus();

          this.$emit('update:shouldFocusEditor', false);
        }, 100);
      });
    },

    async updateConversationAsSeen() {
      await API.conversation.updateConversationAsSeen(
        this.currentCompany.relevant_conversation_id,
      );
    },

    scrollThreadsToBottom() {
      setTimeout(() => {
        const scrollElement = this.$refs.threadContainer;

        if (!scrollElement) return;

        scrollElement.scrollTop = scrollElement.scrollHeight;
      }, 100);
    },

    async reload() {
      if (
        !isEmpty(this.savedConversationPageMessage) ||
        !isEmpty(this.savedConversationDialogMessage)
      ) {
        if (this.conversationPage) {
          this.newMessage = this.savedConversationPageMessage;
          this.$store.dispatch('setSavedConversationPageMessage', '');
        } else {
          this.newMessage = this.savedConversationDialogMessage;
          this.$store.dispatch('setSavedConversationDialogMessage', '');
        }
      }

      await this.refreshConversation();
    },
  },
  async created() {
    this.subscribeToNewMessages();

    if (this.shouldFocusEditor) {
      this.focusEditor();
    }
  },
  beforeDestroy() {
    this.unsubscribeToNewMessages();
  },
  watch: {
    shouldFocusEditor: {
      async handler(value) {
        if (value === true && this.loading === false) {
          this.focusEditor();
        }
      },
    },
    selectedThreadId: {
      async handler() {
        this.loadThreadMessages();
      },
    },
  },
};
</script>

<style scoped lang="postcss">
.divider-block {
  position: relative;
}

.divider-block .date {
  background-color: #f4f6fa;
  position: absolute;
  top: -10px;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  max-width: 100px;
}

.message-container {
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 8px;
  padding-bottom: 8px;
  background-color: white;
  overflow-wrap: break-word;
}

.messages-wrapper {
  height: calc(100vh - 475px);
  max-height: calc(100vh - 475px);
  overflow-x: hidden;
  overflow-y: auto;
}

.messages-wrapper p {
  margin-bottom: 5px;
}

.client-dashboard-wrapper.single-entity .messages-wrapper {
  height: calc(100vh - 425px);
  max-height: calc(100vh - 425px);
}

.message-text {
  font-size: 15px;
}

.message-name {
  font-size: 15px;
  font-weight: bold;
  line-height: 17px;
}

.ql-message-input {
  flex: auto;
  background-color: #fff;
  margin-top: 10px;

  & >>> .ql-toolbar.ql-snow {
    border: none;
    text-align: start;
    background-color: #ebf1f5;
    border-radius: 4px 4px 0px 0px;
    border: 1px solid #ccc;
  }

  & >>> .ql-container.ql-snow {
    border: none;
    font-size: 14px;
  }

  & >>> .ql-container .ql-editor {
    min-height: 80px;
    resize: vertical;
    border: 1px solid #ccc;
    border-top: none;
  }

  & >>> .ql-formats button {
    height: 28px;
    width: 32px;
  }
}
.clickable-chip {
  & >>> .v-chip__content {
    cursor: pointer !important;
  }
}

.action-btn-container {
  border-radius: 0px 0px 4px 4px;
  border: 1px solid #ccc;
  border-top: none;
  background-color: #ebf1f5;
}

.inbox-new-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc(100dvh - 440px);
  height: calc(100dvh - 440px);
  overflow: auto;
  padding: 16px;

  background-color: var(--gray-0);
  border-radius: 6px;
}

.conversation-box__threads {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;

  height: 100%;
  width: 360px;

  background-color: var(--gray-0);
}

.conversation-box__threads__header {
  align-items: center;
  display: flex;
  height: 56px;
  justify-content: space-between;
  padding: 0 16px;

  border-bottom: 1px solid var(--gray-100);
}

.conversation-box__threads__container {
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: calc(100% - 56px);

  border-left: 1px solid var(--gray-100);
}

.conversation-box__threads__content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 12px;
  overflow: auto;
  padding: 12px;
}

.conversation-box__threads__footer {
  align-items: end;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;

  border-top: 1px solid var(--gray-100);
}

.conversation-box__threads__footer form {
  align-items: end;
  gap: 12px;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.conversation-box__threads__footer textarea {
  padding: 16px;
  width: 100%;

  border-radius: 4px;
  border: 1px solid var(--gray-200);
  outline: none;
  transition: all 120ms ease-in-out;

  font-size: var(--small-font-size);
  font-family: var(--font-family-sans);
  line-height: var(--small-line-height);
}

.conversation-box__threads__footer textarea:active,
.conversation-box__threads__footer textarea:focus {
  border-color: var(--gray-400);
}

.reply-textarea--disabled {
  pointer-events: none;

  background-color: var(--gray-100);
}
</style>
